import type { FC } from 'react';


import { AdviceCardImageContainer, Trigger } from '@eneco/custom-components/InspirationAdviceFilter';
import { useTranslation } from '@eneco/i18n';
import { Box, Card, Expandable, Heading, Image, TextLink, Text } from '@eneco/sparky';
import { FocalPointImageField } from '@sitecore/types/InspirationAdviceFilter';
import { FocalPointImage } from '@sitecore/types/lib';
import { LinkField, TextField } from '@sitecore-jss/sitecore-jss-react';

import { AdviceCardSkeleton } from './AdviceCard.skeleton';


interface Props {
  heading: string;
  link?: LinkField;
  image: FocalPointImage;
  isLoading?: boolean;
  text?: TextField['value'];
}

const mapImage = (image: FocalPointImage | FocalPointImageField) => {
  const alt = image.value?.alt ?? '';
  let defaultSrc;
  let widescreenSrc;
  if (image.value !== undefined) {
    if ('fields' in image.value) {
      defaultSrc = image.value.fields.default.value.src;
      widescreenSrc = image.value.fields.widescreen.value.src;
    } else {
      defaultSrc = image.value.formats.find(format => format.format === 'default')?.src;
      widescreenSrc = image.value.formats.find(format => format.format === 'widescreen')?.src;
    }
  }
  return { defaultSrc, widescreenSrc, alt };
};

export const AdviceCard: FC<Props> = ({ heading, link, image, isLoading, text }) => {
  const { t } = useTranslation();

  if (isLoading) return <AdviceCardSkeleton />;

  const { defaultSrc, widescreenSrc, alt } = mapImage(image);

  return (
    <Card>
      {defaultSrc && widescreenSrc ? (
        <AdviceCardImageContainer borderTopLeftRadius="m" borderTopRightRadius="m" overflow="hidden" data-testid="card">
          <Image
            alt={alt}
            hasLazyLoad={true}
            height="200px"
            objectFit="cover"
            sources={{
              xl: defaultSrc,
              lg: widescreenSrc,
              md: defaultSrc,
              sm: widescreenSrc,
            }}
            src={defaultSrc}
            width="100%"
          />
        </AdviceCardImageContainer>
      ) : null}
      <Expandable>
        <Box padding="6" paddingBottom="4" paddingTop="5">
          <Trigger label={heading}>
            <Heading as="h2" size="S">
              {heading}
            </Heading>
          </Trigger>
        </Box>
        <Expandable.Content>
          <Box padding="6" paddingTop="0">
            <Text>{text}</Text>
            {link?.value.href && (
              <>
                <br />
                <TextLink emphasis="high" href={link.value.href}>
                  {link.value.text || t('generalLabels.readMore')}
                </TextLink>
              </>
            )}
          </Box>
        </Expandable.Content>
      </Expandable>
    </Card>
  );
};
